import { SubmissionMethod, SubmissionStatus } from "encamp-prisma/generated/browser";

/**
 * Helpers for the "Waste Stream Registration" submission type, created from the
 * Waste Streams "needs registration" list.
 *
 * The submission *type* is always WASTE_STREAM_REGISTRATION; only the submission
 * *method* varies by the facility's state. States without a confirmed portal
 * mapping fall back to PAPER_FORM. The method is resolved from the table below
 * on every create and is no longer user-selectable, so a state's entry — or its
 * absence — is what staff get.
 */

/** Default method when a state has no confirmed portal mapping below. */
export const WASTE_STREAM_REGISTRATION_DEFAULT_SUBMISSION_METHOD = SubmissionMethod.PAPER_FORM;

/**
 * Where a waste-stream registration is filed, keyed by two-letter state code.
 */
export const WASTE_STREAM_REGISTRATION_SUBMISSION_METHOD_BY_STATE: Partial<
  Record<string, SubmissionMethod>
> = {
  KY: SubmissionMethod.KY_GATEWAY_PORTAL,
  MN: SubmissionMethod.MPCA_E_SERVICES,
  SC: SubmissionMethod.PAPER_FORM,
  TN: SubmissionMethod.PAPER_FORM,
  TX: SubmissionMethod.TX_STEERS_PORTAL,
};

/**
 * Statuses in which a registration submission is finished — filed, or not being
 * filed at all. Anything else is open: it blocks a second submission for the
 * same work and is what the grid links to.
 */
export const FINISHED_REGISTRATION_SUBMISSION_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.FILING_COMPLETE,
  SubmissionStatus.NOT_REPORTING,
];

/** Returns the method a waste-stream registration should use for the given state. */
export function getWasteStreamRegistrationSubmissionMethod(
  state?: string | null
): SubmissionMethod {
  if (!state) {
    return WASTE_STREAM_REGISTRATION_DEFAULT_SUBMISSION_METHOD;
  }
  return (
    WASTE_STREAM_REGISTRATION_SUBMISSION_METHOD_BY_STATE[state.toUpperCase()] ??
    WASTE_STREAM_REGISTRATION_DEFAULT_SUBMISSION_METHOD
  );
}

/** Builds the submission title, e.g. "TX Waste Stream Registration — Spent solvents". */
export function buildWasteStreamRegistrationTitle(state: string, streamName: string): string {
  return `${state.toUpperCase()} Waste Stream Registration — ${streamName.trim()}`;
}

/**
 * Marks a WASTE_STREAM_REGISTRATION submission as asking the state to add
 * federal waste codes to an existing registration, rather than to create a new
 * one. Carried in `Submission.metadata` rather than a distinct SubmissionType:
 * the two kinds share every consumer except the idempotency check and the
 * grid's button label.
 *
 * The target registration itself is not carried here — it lives in the
 * `StateWasteStreamRegistrationSubmission` join row — because that row alone
 * cannot double as the discriminator: a new-registration submission also
 * writes one.
 */
export interface WasteStreamRegistrationUpdateMetadata {
  missingFederalWasteCodes: string[];
}

export interface RegistrationUpdateSubmissionMetadata {
  registrationUpdate: WasteStreamRegistrationUpdateMetadata;
}

/**
 * Narrows a submission's `metadata` JSON without casting through it. Checks the
 * inner shape too, not just the discriminator key: the predicate promises a
 * `missingFederalWasteCodes` array, and a caller reading it off a
 * `{ registrationUpdate: {} }` would get `undefined` from a value the type
 * says cannot be.
 */
export function hasRegistrationUpdateMetadata(
  metadata: unknown
): metadata is RegistrationUpdateSubmissionMetadata {
  if (typeof metadata !== "object" || metadata === null) return false;
  if (!("registrationUpdate" in metadata)) return false;
  const { registrationUpdate } = metadata;
  if (typeof registrationUpdate !== "object" || registrationUpdate === null) return false;
  if (!("missingFederalWasteCodes" in registrationUpdate)) return false;
  return Array.isArray(registrationUpdate.missingFederalWasteCodes);
}

/** e.g. "TX Waste Stream Registration Update — Spent solvents". */
export function buildWasteStreamRegistrationUpdateTitle(state: string, streamName: string): string {
  return `${state.toUpperCase()} Waste Stream Registration Update — ${streamName.trim()}`;
}

/**
 * The notes an update submission is filed with, e.g. "Add federal waste code(s)
 * D002 to registration TX-90210." `registrationLabel` is the state-assigned id
 * where the state has issued one, and the registration's own id otherwise.
 *
 * Shared so the dialog previewing the submission and the resolver creating it
 * cannot spell the same sentence two ways.
 */
export function buildWasteStreamRegistrationUpdateNotes(
  missingFederalWasteCodes: string[],
  registrationLabel: string
): string {
  return `Add federal waste code(s) ${missingFederalWasteCodes.join(", ")} to registration ${registrationLabel}.`;
}
